import { yupResolver } from '@hookform/resolvers/yup'
import { DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentMethod, getEPaymentMethodList } from '@if/core/types'
import { array, boolean, dateOnly, InferType, mixed, number, object, string } from '@if/core/yup'
import { useUser } from '@src/providers/UserProvider'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { DEFAULT_PAYMENT_METHOD } from '@src/types/constants'
import { CustomerPaymentOperation, OperationInformation } from '@src/types/loan/loan-operation'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo } from 'react'
import { Resolver } from 'react-hook-form'

export type EditPaymentFieldCanBeUpdated = (
  payment: EditPaymentSchemaType,
  fieldName: keyof EditPaymentSchemaType,
) => boolean

export const editPaymentFieldIsDisabled: EditPaymentFieldCanBeUpdated = (payment, fieldName) => {
  // TODO: if more fields need special handling, add here
  if (fieldName === 'note') return payment?.isDeleted
  if (fieldName === 'sendSmsReminder')
    return payment?.isDeleted || payment?.isLocked || payment.paymentMethod !== EPaymentMethod.PreAuthorizedPayment
  return payment?.isLocked || payment?.isDeleted
}

export const getEditPaymentSchema = (loanActivationDate: DateOnly, canBypassAcpLock = false) =>
  object({
    id: string().required(),
    paymentNumber: number().optional(),
    isNew: boolean().default(false),
    effectiveDate: dateOnly()
      .required()
      .test('minimum-safe-date', (value, ctx) => {
        const parent = ctx.parent as EditPaymentSchemaType
        if (editPaymentFieldIsDisabled(parent, 'effectiveDate')) return true
        if (!value) return true
        const paymentMethod = parent.paymentMethod ?? DEFAULT_PAYMENT_METHOD
        const minimumSafeDate = SafePaymentActionDateService.getInstance().getMinPaymentDate(
          paymentMethod,
          loanActivationDate,
          canBypassAcpLock,
        )
        if (value < minimumSafeDate) {
          return ctx.createError({
            message: t('loan.payment.editPayments.errors.noBeforeThanDate', { date: minimumSafeDate }),
          })
        }
        return true
      }),
    amount: number().required().min(0.01),
    paymentMethod: mixed<EPaymentMethod>().oneOf(getEPaymentMethodList()).required().default(DEFAULT_PAYMENT_METHOD),
    note: string().notRequired().default(''),
    sendSmsReminder: boolean()
      .default(true)
      .when('paymentMethod', {
        is: EPaymentMethod.PreAuthorizedPayment,
        otherwise: (schema) => schema.default(false).oneOf([false]), // Force false if not PPA
      }),
    isLocked: boolean().required().default(false),
    isDeleted: boolean().required().default(false),
  })

const getEditPaymentsSchema = (schema: ReturnType<typeof getEditPaymentSchema>) =>
  object({
    payments: array(schema).required(),
  })

export type EditPaymentSchemaType = InferType<ReturnType<typeof getEditPaymentSchema>>
export type EditPaymentsSchemaType = InferType<ReturnType<typeof getEditPaymentsSchema>>

const cleanNotePrefix = (note: string | null): string | null => {
  if (!note) return null
  const parts = note.split(':')
  return parts.length > 1 ? parts.slice(1).join(':').trim() : note
}

export const mapEditPaymentDefaultValue = (
  opInfo: OperationInformation<CustomerPaymentOperation>,
  isDeleted = false,
  canBypassAcpLock = false,
): EditPaymentSchemaType => ({
  id: opInfo.operation.id,
  paymentNumber: opInfo.operation.paymentNumber,
  amount: opInfo.operation.amount,
  effectiveDate: opInfo.operation.effectiveDate,
  paymentMethod: opInfo.operation.paymentMethod,
  note: cleanNotePrefix(opInfo.operation.note),
  sendSmsReminder: opInfo.operation.sendSmsReminder,
  isLocked: opInfo.isLocked(canBypassAcpLock),
  isDeleted,
  isNew: false,
})

/**
 * We are loading the default values asynchronously to avoid blocking the main thread
 * We use queueMicrotask to yield to the main thread before computing the default values
 * This is useful when the computation is heavy and we want to keep the UI responsive
 * @param simulation
 * @param selectedPaymentIds
 * @returns
 */
const loadDefaultValuesAsync = (
  simulation: CalendarSimulation,
  selectedPaymentIds: Set<string>,
  isDeleted = false,
  canBypassAcpLock = false,
): Promise<EditPaymentsSchemaType> =>
  new Promise<EditPaymentsSchemaType>((resolve) => {
    queueMicrotask(() => {
      const payments: EditPaymentSchemaType[] = simulation.customerPaymentOperations.reduce<EditPaymentSchemaType[]>(
        (acc, opInfo) => {
          if (!selectedPaymentIds.has(opInfo.operation.id)) return acc
          acc.push(mapEditPaymentDefaultValue(opInfo, isDeleted, canBypassAcpLock))
          return acc
        },
        [],
      )
      resolve({
        payments,
      })
    })
  })

interface EditPaymentsFormResults {
  resolver: Resolver<EditPaymentsSchemaType>
  defaultValues: Promise<EditPaymentsSchemaType>
}

export function useEditPaymentsForm(
  simulation: CalendarSimulation,
  selectedPaymentIds: Set<string>,
  isDeleted = false,
): EditPaymentsFormResults {
  const user = useUser()
  const canBypassAcpLock = user?.rights.canEditLockedPpaPayments ?? false
  return useMemo<EditPaymentsFormResults>(() => {
    const paymentSchema = getEditPaymentSchema(simulation.fullLoan.loan.activationOn, canBypassAcpLock)
    return {
      resolver: yupResolver(getEditPaymentsSchema(paymentSchema)),
      defaultValues: loadDefaultValuesAsync(simulation, selectedPaymentIds, isDeleted, canBypassAcpLock),
    }
  }, [simulation, selectedPaymentIds, isDeleted, canBypassAcpLock])
}
